import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useAccounts } from "../../components/Common/HelperComponents/GetDataFromStore";
import {
  createPartnerTaskOrder,
  getOrganizationCustomer,
  getPartnerCustomer,
  getPartnerTaskOrder,
  updatePartnerTaskOrder,
} from "../../helpers/partnerCentral_helper";
import { isMSP } from "../../helpers/util_helper";
import DueDateField from "../Compliance/AuditHub/DueDateField";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_WHITE,
  accentOutlinedButtonSx,
  AuditHubThemeProvider,
  auditHubPageSx,
  backButtonSx,
  outlinedButtonSx,
  primaryButtonSx,
  tableShellSx,
} from "../Compliance/AuditHub/theme";
import {
  autocompleteSlotProps,
  buildSelectProps,
  fieldSx,
  menuItemSx,
  selectFieldSx,
} from "./formFieldStyles";
import {
  formTableActionBtnSx,
  formTableActionsWrapSx,
  formTableBodyCellSx,
  formTableCardSx,
  formTableDeleteBtnSx,
  formTableHeadCellSx,
} from "./formTableStyles";
import {
  COMPLIANCE_CATEGORY_OPTIONS,
  OPTION_PERIOD_OPTIONS,
  PAYMENT_OFFICE_OPTIONS,
  buildTaskOrderPayload,
  validateActiveTaskOrder,
} from "./taskOrderPayload";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const accountChipSx = {
  height: 22,
  maxWidth: "100%",
  fontSize: 11,
  fontWeight: 500,
  fontFamily: "monospace",
  backgroundColor: AH_WHITE,
  border: `1px solid ${AH_BORDER}`,
  color: AH_TEXT,
  "& .MuiChip-label": {
    px: 0.75,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .MuiChip-deleteIcon": {
    fontSize: 14,
    color: AH_MUTED,
    "&:hover": { color: AH_TEXT },
  },
};

const selectAllChipSx = (active) => ({
  height: 32,
  flexShrink: 0,
  alignSelf: { xs: "stretch", sm: "flex-start" },
  mt: { sm: 0.25 },
  fontSize: 12,
  fontWeight: 600,
  borderRadius: "8px",
  border: `1px solid ${AH_ACCENT}`,
  color: AH_ACCENT,
  backgroundColor: active ? "#fff4eb" : AH_WHITE,
  "&:hover": {
    borderColor: AH_ACCENT_HOVER,
    color: AH_ACCENT_HOVER,
    backgroundColor: "#fff4eb",
  },
  "&.Mui-disabled": {
    borderColor: AH_BORDER,
    color: AH_MUTED,
    backgroundColor: AH_WHITE,
  },
});

const distinctAccountIds = (values) => [
  ...new Set(
    (values || [])
      .map((id) => String(id || "").trim())
      .filter((id) => /^\d{12}$/.test(id))
  ),
];

const accountOptionLabel = (option) => {
  const number = option?.accountNumber || "";
  const name =
    option?.accountName && option.accountName !== number ? option.accountName : "";
  return name ? `${name} (${number})` : number;
};

const CUSTOMER_PATH = "/partner-central/customer";

/** Safe in-app return path (FinOps Customer Management may deep-link here). */
const resolveReturnPath = (value) => {
  const path = String(value || "").trim();
  if (!path.startsWith("/") || path.startsWith("//")) return CUSTOMER_PATH;
  if (path.includes("://")) return CUSTOMER_PATH;
  return path;
};

const labelSx = {
  display: "block",
  mb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: 1.4,
};

const sectionSx = {
  fontSize: 14,
  fontWeight: 700,
  color: AH_TEXT,
  letterSpacing: "-0.01em",
};

const parseNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,%/]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const formatMoneyDisplay = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return value || "";
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const emptyClinRow = () => ({
  id: `clin-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  clinNumber: "",
  slinIdentifier: "",
  description: "",
  quantity: "",
  unitPrice: "",
  fundedAmount: "",
  accountingCode: "",
});

const emptyForm = () => ({
  taskOrderNumber: "",
  referenceId: "",
  orderDate: "",
  modificationNumber: "00",
  parentContractNumber: "",
  subAgencyName: "",
  koName: "",
  koEmail: "",
  corName: "",
  corEmail: "",
  startDate: "",
  endDate: "",
  optionPeriod: "",
  totalObligatedAmount: "",
  lineOfAccounting: "",
  paymentOfficeMethod: "",
  naicsCode: "",
  clinRows: [],
  accountNumbers: [],
  complianceCategory: "",
  placeOfPerformance: "",
  statementOfWork: "",
});

const formFromTaskOrder = (order) => {
  const clinRows = Array.isArray(order.clin_slin_schedule) && order.clin_slin_schedule.length
    ? order.clin_slin_schedule.map((row, index) => ({
        id: `clin-loaded-${index}`,
        clinNumber: row.clin_number || "",
        slinIdentifier: row.slin_identifier || "",
        description: row.description || "",
        quantity: row.quantity != null ? String(row.quantity) : "",
        unitPrice: row.unit_price != null ? String(row.unit_price) : "",
        fundedAmount: row.funded_amount != null ? String(row.funded_amount) : "",
        accountingCode: row.accounting_code || "",
      }))
    : [];

  return {
    taskOrderNumber: order.task_order_number || "",
    referenceId: order.reference_id || "",
    orderDate: order.order_date || "",
    modificationNumber: order.modification_number || "00",
    parentContractNumber: order.parent_contract_number || "",
    subAgencyName: order.sub_agency_name || "",
    koName: order.contracting_officer_name || "",
    koEmail: order.contracting_officer_email || "",
    corName: order.contracting_officer_representative_name || "",
    corEmail: order.contracting_officer_representative_email || "",
    startDate: order.start_date || "",
    endDate: order.end_date || "",
    optionPeriod: order.option_period || "",
    totalObligatedAmount:
      order.total_obligated_amount != null ? formatMoneyDisplay(order.total_obligated_amount) : "",
    lineOfAccounting: order.line_of_accounting || "",
    paymentOfficeMethod: order.payment_office_method || "",
    naicsCode: order.naics_code || "",
    clinRows,
    accountNumbers: distinctAccountIds(order.accountNumbers),
    complianceCategory: order.compliance_category || "",
    placeOfPerformance: order.place_of_performance || "",
    statementOfWork: order.statement_of_work_instructions || "",
  };
};

const FieldLabel = ({ htmlFor, children }) => (
  <Typography component="label" htmlFor={htmlFor} sx={labelSx}>
    {children}
  </Typography>
);

const AddTaskOrder = () => {
  const history = useHistory();
  const location = useLocation();
  const allowed = isMSP();
  const accounts = useAccounts();
  const search = useMemo(() => new URLSearchParams(location.search || ""), [location.search]);
  const customerId = search.get("customerId");
  const taskOrderId = search.get("id");
  const leavePath = resolveReturnPath(search.get("returnTo"));

  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [customerError, setCustomerError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [orderLoading, setOrderLoading] = useState(Boolean(taskOrderId));
  const [clinDialogOpen, setClinDialogOpen] = useState(false);
  const [clinDraft, setClinDraft] = useState(emptyClinRow);
  const [clinDraftErrors, setClinDraftErrors] = useState({});
  const [editingClinId, setEditingClinId] = useState(null);

  useEffect(() => {
    document.title = taskOrderId
      ? "Edit Task Order | Partner Central | CloudNova App"
      : "Add Task Order | Partner Central | CloudNova App";
  }, [taskOrderId]);

  useEffect(() => {
    if (!allowed) history.replace("/dashboard");
  }, [allowed, history]);

  useEffect(() => {
    if (!allowed || !customerId) {
      setCustomerLoading(false);
      setCustomerError("Select a customer before creating a task order.");
      return undefined;
    }
    let cancelled = false;
    setCustomerLoading(true);
    setCustomerError("");
    getPartnerCustomer(customerId)
      .then(async (record) => {
        if (cancelled) return;
        // Canonical accounts live on Java GET /organization/customer.
        const orgId = record?.organization_id;
        if (orgId == null || orgId === "") {
          setCustomer(record);
          return;
        }
        try {
          const org = await getOrganizationCustomer(orgId);
          if (cancelled) return;
          const accountNumbers = distinctAccountIds(
            Array.isArray(org?.accountNumbers)
              ? org.accountNumbers
              : record.aws_account_numbers
          );
          setCustomer({ ...record, accountNumbers });
        } catch (_error) {
          if (cancelled) return;
          setCustomer({
            ...record,
            accountNumbers: distinctAccountIds(record.aws_account_numbers),
          });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setCustomer(null);
        setCustomerError(error?.message || "Unable to load customer.");
      })
      .finally(() => {
        if (!cancelled) setCustomerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, customerId]);

  useEffect(() => {
    if (!allowed || !taskOrderId) {
      setOrderLoading(false);
      return undefined;
    }
    let cancelled = false;
    setOrderLoading(true);
    getPartnerTaskOrder(taskOrderId)
      .then((order) => {
        if (cancelled) return;
        setForm(formFromTaskOrder(order));
      })
      .catch((error) => {
        if (cancelled) return;
        setFormError(error?.message || "Unable to load task order.");
      })
      .finally(() => {
        if (!cancelled) setOrderLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, taskOrderId]);

  const accountNameByNumber = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(accounts)) return map;
    accounts.forEach((account) => {
      const number = String(account?.accountNumber ?? "").trim();
      if (!/^\d{12}$/.test(number)) return;
      const name = String(account?.accountName || "").trim();
      if (name) map.set(number, name);
    });
    return map;
  }, [accounts]);

  const customerAccountOptions = useMemo(() => {
    const ids = distinctAccountIds(
      Array.isArray(customer?.accountNumbers)
        ? customer.accountNumbers
        : customer?.aws_account_numbers
    );
    return ids.map((accountNumber) => ({
      accountNumber,
      accountName: accountNameByNumber.get(accountNumber) || "",
    }));
  }, [customer, accountNameByNumber]);

  const selectedAccountOptions = useMemo(() => {
    const selected = new Set(distinctAccountIds(form.accountNumbers));
    const fromOptions = customerAccountOptions.filter((opt) =>
      selected.has(opt.accountNumber)
    );
    // Keep hydrated selections visible even if not yet in the customer roster.
    const known = new Set(fromOptions.map((opt) => opt.accountNumber));
    const orphans = [...selected]
      .filter((id) => !known.has(id))
      .map((accountNumber) => ({
        accountNumber,
        accountName: accountNameByNumber.get(accountNumber) || "",
      }));
    return [...fromOptions, ...orphans];
  }, [customerAccountOptions, form.accountNumbers, accountNameByNumber]);

  const allCustomerAccountsSelected =
    customerAccountOptions.length > 0 &&
    customerAccountOptions.every((opt) =>
      (form.accountNumbers || []).includes(opt.accountNumber)
    );

  const fundedTotal = useMemo(() => {
    return (form.clinRows || []).reduce((sum, row) => {
      const amount = parseNumber(row.fundedAmount);
      return sum + (amount != null && !Number.isNaN(amount) ? amount : 0);
    }, 0);
  }, [form.clinRows]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const openClinDialog = () => {
    setEditingClinId(null);
    setClinDraft(emptyClinRow());
    setClinDraftErrors({});
    setClinDialogOpen(true);
  };

  const openEditClinDialog = (row) => {
    setEditingClinId(row.id);
    setClinDraft({
      id: row.id,
      clinNumber: row.clinNumber || "",
      slinIdentifier: row.slinIdentifier || "",
      description: row.description || "",
      quantity: row.quantity || "",
      unitPrice: row.unitPrice || "",
      fundedAmount: row.fundedAmount || "",
      accountingCode: row.accountingCode || "",
    });
    setClinDraftErrors({});
    setClinDialogOpen(true);
  };

  const closeClinDialog = () => {
    setClinDialogOpen(false);
    setClinDraft(emptyClinRow());
    setClinDraftErrors({});
    setEditingClinId(null);
  };

  const setClinDraftField = (key, value) => {
    setClinDraft((prev) => ({ ...prev, [key]: value }));
    setClinDraftErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateClinDraft = () => {
    const next = {};
    const blank =
      !clinDraft.clinNumber?.trim() &&
      !clinDraft.slinIdentifier?.trim() &&
      !clinDraft.description?.trim() &&
      !clinDraft.quantity?.trim() &&
      !clinDraft.unitPrice?.trim() &&
      !clinDraft.fundedAmount?.trim() &&
      !clinDraft.accountingCode?.trim();
    if (blank) {
      next.clinNumber = editingClinId
        ? "Enter CLIN details to save this row."
        : "Enter CLIN details to create a CLIN row.";
      return next;
    }
    if (!clinDraft.clinNumber?.trim()) next.clinNumber = "Enter a CLIN number.";
    if (!clinDraft.description?.trim()) next.description = "Enter a description.";
    const funded = parseNumber(clinDraft.fundedAmount);
    if (clinDraft.fundedAmount == null || String(clinDraft.fundedAmount).trim() === "") {
      next.fundedAmount = "Enter a funded amount.";
    } else if (Number.isNaN(funded) || funded < 0) {
      next.fundedAmount = "Enter a valid amount.";
    }
    const qty = parseNumber(clinDraft.quantity);
    if (clinDraft.quantity?.trim() && (Number.isNaN(qty) || qty < 0)) {
      next.quantity = "Enter a valid quantity.";
    }
    const unit = parseNumber(clinDraft.unitPrice);
    if (clinDraft.unitPrice?.trim() && (Number.isNaN(unit) || unit < 0)) {
      next.unitPrice = "Enter a valid unit price.";
    }
    return next;
  };

  const saveClinDraft = () => {
    const nextErrors = validateClinDraft();
    setClinDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const quantity = clinDraft.quantity.trim();
    const unitPrice = clinDraft.unitPrice.trim()
      ? formatMoneyDisplay(clinDraft.unitPrice)
      : "";
    const fundedAmount = clinDraft.fundedAmount.trim()
      ? formatMoneyDisplay(clinDraft.fundedAmount)
      : "";

    const saved = {
      ...clinDraft,
      id: editingClinId || clinDraft.id,
      clinNumber: clinDraft.clinNumber.trim(),
      slinIdentifier: clinDraft.slinIdentifier.trim(),
      description: clinDraft.description.trim(),
      quantity,
      unitPrice,
      fundedAmount,
      accountingCode: clinDraft.accountingCode.trim(),
    };

    setForm((prev) => ({
      ...prev,
      clinRows: editingClinId
        ? prev.clinRows.map((row) => (row.id === editingClinId ? saved : row))
        : [...prev.clinRows, saved],
    }));
    setErrors((prev) => {
      if (!prev.clinRows && !(editingClinId && prev.clinRowErrors?.[editingClinId])) return prev;
      const next = { ...prev };
      if (next.clinRows) delete next.clinRows;
      if (editingClinId && next.clinRowErrors?.[editingClinId]) {
        next.clinRowErrors = { ...next.clinRowErrors };
        delete next.clinRowErrors[editingClinId];
        if (!Object.keys(next.clinRowErrors).length) delete next.clinRowErrors;
      }
      return next;
    });
    closeClinDialog();
  };

  const removeClinRow = (rowId) => {
    setForm((prev) => ({
      ...prev,
      clinRows: prev.clinRows.filter((row) => row.id !== rowId),
    }));
    setErrors((prev) => {
      if (!prev.clinRowErrors?.[rowId] && !prev.clinRows) return prev;
      const next = { ...prev };
      if (next.clinRows) delete next.clinRows;
      if (next.clinRowErrors?.[rowId]) {
        next.clinRowErrors = { ...next.clinRowErrors };
        delete next.clinRowErrors[rowId];
        if (!Object.keys(next.clinRowErrors).length) delete next.clinRowErrors;
      }
      return next;
    });
  };

  const formatClinMoney = (value) => {
    const amount = parseNumber(value);
    if (amount == null || Number.isNaN(amount)) return value?.trim() ? String(value) : "—";
    return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  };

  const leave = () => history.push(leavePath);

  const validateDraft = () => {
    const next = {};
    if (form.koEmail?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.koEmail.trim())) {
      next.koEmail = "Enter a valid email address.";
    }
    if (form.corEmail?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.corEmail.trim())) {
      next.corEmail = "Enter a valid email address.";
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      next.endDate = "End date must be on or after the start date.";
    }
    const naics = String(form.naicsCode || "").trim();
    if (naics && !/^\d{6}$/.test(naics)) next.naicsCode = "NAICS code must be six digits.";
    return next;
  };

  const submit = async (status) => {
    if (saving || !customer?.id) return;
    const draft = status === "draft";
    let nextErrors = validateDraft();
    if (!draft) {
      nextErrors = { ...nextErrors, ...validateActiveTaskOrder(form, customer) };
    }
    setErrors(nextErrors);
    setFormError("");
    if (Object.keys(nextErrors).length) {
      document.querySelector("[aria-invalid='true']")?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    const payload = buildTaskOrderPayload(form, status, customer);
    setSaving(true);
    try {
      const saved = taskOrderId
        ? await updatePartnerTaskOrder(taskOrderId, payload)
        : await createPartnerTaskOrder(payload);
      const number = saved?.task_order_number || form.taskOrderNumber.trim() || "Task order";
      const notice = draft
        ? `Task order draft saved: ${number}.`
        : taskOrderId
          ? `Task order updated: ${number}.`
          : `Task order created: ${number}.`;
      history.push(leavePath, { notice });
    } catch (error) {
      setSaving(false);
      setFormError(error?.message || "Unable to save the task order.");
    }
  };

  const helperProps = (key) => ({ sx: { mx: 0, color: errors[key] ? undefined : AH_MUTED } });

  const customerBadge = customer
    ? `${customer.organization_name || "Customer"}${customer.customer_code ? ` — ${customer.customer_code}` : ""}`
    : "—";

  if (!allowed) return null;

  const pageBusy = customerLoading || orderLoading;

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid className="px-0">
          <Box sx={{ pt: 1.5, pb: 4, width: "100%", ...auditHubPageSx }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.5,
              }}
            >
              <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED, minWidth: 0 }} aria-label="Breadcrumb">
                <Link component="button" type="button" underline="hover" color="inherit" onClick={leave} sx={{ fontSize: 13 }}>
                  Customer
                </Link>
                <Typography sx={{ fontSize: 13, color: AH_TEXT }}>
                  {taskOrderId ? "Edit Task Order" : "Add Task Order"}
                </Typography>
              </Breadcrumbs>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={leave}
                sx={backButtonSx}
              >
                Back
              </Button>
            </Box>

            <Box sx={tableShellSx}>
              <Typography
                component="h1"
                sx={{
                  m: 0,
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                }}
              >
                {taskOrderId ? "Edit Task Order" : "Add Task Order"}
              </Typography>
              <Typography sx={{ mt: 0.25, mb: 2.5, fontSize: 13, color: AH_MUTED }}>
                Create a federal task order linked to a customer contract with CLIN schedule and funding details.
              </Typography>

              {pageBusy ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={32} sx={{ color: AH_ACCENT }} />
                </Box>
              ) : customerError ? (
                <Typography sx={{ fontSize: 13, color: "#d32f2f" }} role="alert">
                  {customerError}
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="taskOrderNumber">Task Order Number</FieldLabel>
                      <TextField
                        id="taskOrderNumber"
                        size="small"
                        fullWidth
                        value={form.taskOrderNumber}
                        onChange={(event) => setField("taskOrderNumber", event.target.value)}
                        placeholder="Enter the unique alpha-numeric order identifier"
                        error={Boolean(errors.taskOrderNumber)}
                        helperText={errors.taskOrderNumber || ""}
                        FormHelperTextProps={helperProps("taskOrderNumber")}
                        inputProps={{ "aria-invalid": Boolean(errors.taskOrderNumber) || undefined }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="referenceId">Reference ID</FieldLabel>
                      <TextField
                        id="referenceId"
                        size="small"
                        fullWidth
                        value={form.referenceId}
                        onChange={(event) => setField("referenceId", event.target.value)}
                        placeholder="Enter your Unique internal reference ID"
                        sx={fieldSx}
                      />
                    </Box>
                    <Box
                      sx={{
                        gridColumn: "1 / -1",
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                          md: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="orderDate">Order Date</FieldLabel>
                        <DueDateField
                          id="orderDate"
                          value={form.orderDate}
                          onChange={(value) => setField("orderDate", value)}
                          fullWidth
                          showLabel={false}
                          size="small"
                          minDate={null}
                          ariaLabel="Order date"
                          placeholder="Select the Order Date"
                          error={Boolean(errors.orderDate)}
                          helperText={errors.orderDate || ""}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="modificationNumber">Modification Number</FieldLabel>
                        <TextField
                          id="modificationNumber"
                          size="small"
                          fullWidth
                          value={form.modificationNumber}
                          onChange={(event) => setField("modificationNumber", event.target.value)}
                          helperText="Defaults to 00 for base awards; increment for subsequent mods"
                          FormHelperTextProps={helperProps("modificationNumber")}
                          sx={fieldSx}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="parentContractNumber">Parent Contract Number</FieldLabel>
                        <TextField
                          id="parentContractNumber"
                          size="small"
                          fullWidth
                          value={form.parentContractNumber}
                          onChange={(event) => setField("parentContractNumber", event.target.value)}
                          placeholder="e.g., GS-35F-0265X"
                          error={Boolean(errors.parentContractNumber)}
                          helperText={errors.parentContractNumber || ""}
                          FormHelperTextProps={helperProps("parentContractNumber")}
                          inputProps={{ "aria-invalid": Boolean(errors.parentContractNumber) || undefined }}
                          sx={fieldSx}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <FieldLabel htmlFor="customerBadge">Customer</FieldLabel>
                        <TextField
                          id="customerBadge"
                          size="small"
                          fullWidth
                          value={customerBadge}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <LockOutlinedIcon sx={{ fontSize: 16, color: AH_MUTED }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            ...fieldSx,
                            "& .MuiOutlinedInput-root": {
                              ...fieldSx["& .MuiOutlinedInput-root"],
                              backgroundColor: "#f8fafc",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="subAgencyName">Sub-Agency / Component Office</FieldLabel>
                      <TextField
                        id="subAgencyName"
                        size="small"
                        fullWidth
                        value={form.subAgencyName}
                        onChange={(event) => setField("subAgencyName", event.target.value)}
                        placeholder="e.g., CISA — Cybersecurity & Infrastructure"
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="optionPeriod">Option Periods</FieldLabel>
                      <TextField
                        id="optionPeriod"
                        select
                        size="small"
                        fullWidth
                        value={form.optionPeriod || ""}
                        onChange={(event) => setField("optionPeriod", event.target.value)}
                        sx={selectFieldSx(Boolean(form.optionPeriod))}
                        SelectProps={buildSelectProps({ placeholder: "None" })}
                      >
                        <MenuItem value="" sx={menuItemSx}>
                          None
                        </MenuItem>
                        {OPTION_PERIOD_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option} sx={menuItemSx}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="koName">Contracting Officer (KO) Name</FieldLabel>
                      <TextField
                        id="koName"
                        size="small"
                        fullWidth
                        value={form.koName}
                        onChange={(event) => setField("koName", event.target.value)}
                        placeholder="e.g., Alex Morgan"
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="koEmail">Contracting Officer (KO) Email</FieldLabel>
                      <TextField
                        id="koEmail"
                        size="small"
                        fullWidth
                        value={form.koEmail}
                        onChange={(event) => setField("koEmail", event.target.value)}
                        placeholder="e.g., alex.morgan@agency.gov"
                        error={Boolean(errors.koEmail)}
                        helperText={errors.koEmail || ""}
                        FormHelperTextProps={helperProps("koEmail")}
                        inputProps={{ "aria-invalid": Boolean(errors.koEmail) || undefined }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="corName">COR Full Name</FieldLabel>
                      <TextField
                        id="corName"
                        size="small"
                        fullWidth
                        value={form.corName}
                        onChange={(event) => setField("corName", event.target.value)}
                        placeholder="e.g., Jamie Lee"
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="corEmail">COR Email</FieldLabel>
                      <TextField
                        id="corEmail"
                        size="small"
                        fullWidth
                        value={form.corEmail}
                        onChange={(event) => setField("corEmail", event.target.value)}
                        placeholder="e.g., jamie.lee@agency.gov"
                        error={Boolean(errors.corEmail)}
                        helperText={errors.corEmail || ""}
                        FormHelperTextProps={helperProps("corEmail")}
                        inputProps={{ "aria-invalid": Boolean(errors.corEmail) || undefined }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="startDate">Task Order Start Date</FieldLabel>
                      <DueDateField
                        id="startDate"
                        value={form.startDate}
                        onChange={(value) => {
                          setField("startDate", value);
                          if (form.endDate && value && form.endDate < value) setField("endDate", "");
                        }}
                        fullWidth
                        showLabel={false}
                        size="small"
                        minDate={customer?.contract_start_date || null}
                        maxDate={customer?.contract_end_date || undefined}
                        ariaLabel="Task order start date"
                        placeholder="Select the Task Order Start Date"
                        error={Boolean(errors.startDate)}
                        helperText={errors.startDate || ""}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="endDate">Task Order End Date</FieldLabel>
                      <DueDateField
                        id="endDate"
                        value={form.endDate}
                        onChange={(value) => setField("endDate", value)}
                        fullWidth
                        showLabel={false}
                        size="small"
                        minDate={form.startDate || customer?.contract_start_date || null}
                        maxDate={customer?.contract_end_date || undefined}
                        ariaLabel="Task order end date"
                        placeholder="Select the Task Order End Date"
                        error={Boolean(errors.endDate)}
                        helperText={errors.endDate || ""}
                      />
                    </Box>
                  </Box>

                  <Typography component="h3" sx={{ ...sectionSx, mt: 3.5, mb: 1.5 }}>
                    Funding Details
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="totalObligatedAmount">Total Obligated Amount ($)</FieldLabel>
                      <TextField
                        id="totalObligatedAmount"
                        size="small"
                        fullWidth
                        value={form.totalObligatedAmount}
                        onChange={(event) => setField("totalObligatedAmount", event.target.value)}
                        onBlur={() => {
                          if (form.totalObligatedAmount.trim()) {
                            setField("totalObligatedAmount", formatMoneyDisplay(form.totalObligatedAmount));
                          }
                        }}
                        placeholder="0.00"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        error={Boolean(errors.totalObligatedAmount)}
                        helperText={errors.totalObligatedAmount || ""}
                        FormHelperTextProps={helperProps("totalObligatedAmount")}
                        inputProps={{ "aria-invalid": Boolean(errors.totalObligatedAmount) || undefined }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="lineOfAccounting">Line of Accounting (LOA)</FieldLabel>
                      <TextField
                        id="lineOfAccounting"
                        size="small"
                        fullWidth
                        value={form.lineOfAccounting}
                        onChange={(event) => setField("lineOfAccounting", event.target.value)}
                        placeholder="e.g., 97X4930 TF 2026"
                        error={Boolean(errors.lineOfAccounting)}
                        helperText={errors.lineOfAccounting || ""}
                        FormHelperTextProps={helperProps("lineOfAccounting")}
                        inputProps={{ "aria-invalid": Boolean(errors.lineOfAccounting) || undefined }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="paymentOfficeMethod">Payment Office & Method</FieldLabel>
                      <TextField
                        id="paymentOfficeMethod"
                        select
                        size="small"
                        fullWidth
                        value={form.paymentOfficeMethod || ""}
                        onChange={(event) => setField("paymentOfficeMethod", event.target.value)}
                        sx={selectFieldSx(Boolean(form.paymentOfficeMethod))}
                        SelectProps={buildSelectProps({ placeholder: "Default / unset" })}
                      >
                        <MenuItem value="" sx={menuItemSx}>
                          Default / unset
                        </MenuItem>
                        {PAYMENT_OFFICE_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option} sx={menuItemSx}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mt: 3.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography component="h3" sx={{ ...sectionSx, mt: 0, mb: 0 }}>
                      CLIN / SLIN Schedule
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                      onClick={openClinDialog}
                      sx={{ ...accentOutlinedButtonSx, height: 36, minHeight: 36, px: 1.5 }}
                    >
                      Add CLIN
                    </Button>
                  </Box>
                  <Box sx={{ mb: 2, maxWidth: { sm: "50%" } }}>
                    <FieldLabel htmlFor="naicsCode">NAICS Code</FieldLabel>
                    <TextField
                      id="naicsCode"
                      size="small"
                      fullWidth
                      value={form.naicsCode}
                      onChange={(event) => setField("naicsCode", event.target.value)}
                      placeholder="e.g., 541512"
                      error={Boolean(errors.naicsCode)}
                      helperText={errors.naicsCode || "Six digits when provided"}
                      FormHelperTextProps={helperProps("naicsCode")}
                      inputProps={{ "aria-invalid": Boolean(errors.naicsCode) || undefined }}
                      sx={fieldSx}
                    />
                  </Box>
                  {errors.clinRows ? (
                    <Typography sx={{ mb: 1, fontSize: 12, color: "#d32f2f" }} role="alert">
                      {errors.clinRows}
                    </Typography>
                  ) : null}
                  <TableContainer sx={{ ...formTableCardSx, overflowX: "auto", mb: 1 }}>
                    <Table size="small" aria-label="CLIN SLIN schedule">
                      <TableHead>
                        <TableRow>
                          {["CLIN", "SLIN", "DESCRIPTION", "QTY", "UNIT PRICE ($)", "FUNDED ($)", "ACCOUNTING CODE", "ACTIONS"].map(
                            (heading) => (
                              <TableCell key={heading} sx={formTableHeadCellSx}>
                                {heading}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {form.clinRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              sx={{ ...formTableBodyCellSx, py: 4, borderBottom: 0, color: AH_MUTED }}
                            >
                              No CLIN rows yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          form.clinRows.map((row) => {
                            const rowErrors = errors.clinRowErrors?.[row.id] || {};
                            const hasRowError = Boolean(
                              rowErrors.clinNumber ||
                                rowErrors.description ||
                                rowErrors.quantity ||
                                rowErrors.unitPrice ||
                                rowErrors.fundedAmount
                            );
                            return (
                              <TableRow key={row.id} hover>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 88 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 600,
                                      color: hasRowError && rowErrors.clinNumber ? "#d32f2f" : AH_TEXT,
                                    }}
                                  >
                                    {row.clinNumber || "—"}
                                  </Typography>
                                  {rowErrors.clinNumber ? (
                                    <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>
                                      {rowErrors.clinNumber}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 88, color: AH_MUTED }}>
                                  {row.slinIdentifier || "—"}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 180, textAlign: "left" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      color: rowErrors.description ? "#d32f2f" : "#334155",
                                    }}
                                  >
                                    {row.description || "—"}
                                  </Typography>
                                  {rowErrors.description ? (
                                    <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>
                                      {rowErrors.description}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 72 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      color: rowErrors.quantity ? "#d32f2f" : "#334155",
                                    }}
                                  >
                                    {row.quantity || "—"}
                                  </Typography>
                                  {rowErrors.quantity ? (
                                    <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>
                                      {rowErrors.quantity}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 110 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 600,
                                      color: rowErrors.unitPrice ? "#d32f2f" : AH_TEXT,
                                    }}
                                  >
                                    {formatClinMoney(row.unitPrice)}
                                  </Typography>
                                  {rowErrors.unitPrice ? (
                                    <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>
                                      {rowErrors.unitPrice}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 110 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 600,
                                      color: rowErrors.fundedAmount ? "#d32f2f" : AH_TEXT,
                                    }}
                                  >
                                    {formatClinMoney(row.fundedAmount)}
                                  </Typography>
                                  {rowErrors.fundedAmount ? (
                                    <Typography sx={{ mt: 0.25, fontSize: 12, color: "#d32f2f" }}>
                                      {rowErrors.fundedAmount}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, minWidth: 100, color: AH_MUTED }}>
                                  {row.accountingCode || "—"}
                                </TableCell>
                                <TableCell sx={{ ...formTableBodyCellSx, width: 96, whiteSpace: "nowrap" }}>
                                  <Box sx={formTableActionsWrapSx}>
                                    <Tooltip title="Edit CLIN">
                                      <IconButton
                                        aria-label={`Edit CLIN ${row.clinNumber || "row"}`}
                                        onClick={() => openEditClinDialog(row)}
                                        size="small"
                                        sx={formTableActionBtnSx}
                                      >
                                        <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove CLIN">
                                      <IconButton
                                        aria-label={`Remove CLIN ${row.clinNumber || "row"}`}
                                        onClick={() => removeClinRow(row.id)}
                                        size="small"
                                        sx={formTableDeleteBtnSx}
                                      >
                                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                        {form.clinRows.length > 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              sx={{ ...formTableBodyCellSx, fontWeight: 600, color: AH_TEXT, borderBottom: 0 }}
                            >
                              Total Funded
                            </TableCell>
                            <TableCell
                              sx={{ ...formTableBodyCellSx, fontWeight: 700, color: AH_TEXT, borderBottom: 0 }}
                            >
                              ${fundedTotal.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell colSpan={2} sx={{ ...formTableBodyCellSx, borderBottom: 0 }} />
                          </TableRow>
                        ) : null}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography component="h3" sx={{ ...sectionSx, mt: 3.5, mb: 1.5 }}>
                    Account & Compliance
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <Box sx={{ minWidth: 0, gridColumn: { sm: "1 / -1" } }}>
                      <FieldLabel htmlFor="accountNumbers">AWS Accounts</FieldLabel>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "stretch", sm: "flex-start" },
                          gap: 1,
                        }}
                      >
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          id="accountNumbers"
                          options={customerAccountOptions}
                          value={selectedAccountOptions}
                          onChange={(_event, value) => {
                            setField(
                              "accountNumbers",
                              distinctAccountIds(
                                (value || []).map((opt) => opt.accountNumber)
                              )
                            );
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.accountNumber === value.accountNumber
                          }
                          getOptionLabel={accountOptionLabel}
                          slotProps={autocompleteSlotProps}
                          noOptionsText={
                            customerAccountOptions.length
                              ? "No accounts match your search"
                              : "No authorized accounts on this customer"
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.accountNumber}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  minWidth: 0,
                                  width: "100%",
                                }}
                              >
                                <Checkbox
                                  icon={checkboxIcon}
                                  checkedIcon={checkboxCheckedIcon}
                                  checked={selected}
                                  tabIndex={-1}
                                  disableRipple
                                  sx={{
                                    p: 0.25,
                                    color: AH_MUTED,
                                    "&.Mui-checked": { color: AH_ACCENT },
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "0.8125rem",
                                    fontWeight: 500,
                                    color: AH_TEXT,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                  }}
                                >
                                  {accountOptionLabel(option)}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const { key, ...tagProps } = getTagProps({ index });
                              return (
                                <Chip
                                  key={key}
                                  {...tagProps}
                                  size="small"
                                  label={option.accountNumber}
                                  title={accountOptionLabel(option)}
                                  sx={accountChipSx}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder={
                                selectedAccountOptions.length
                                  ? ""
                                  : customerAccountOptions.length
                                    ? "Search account number or name"
                                    : "No authorized accounts on this customer"
                              }
                              error={Boolean(errors.accountNumbers)}
                              inputProps={{
                                ...params.inputProps,
                                "aria-invalid": Boolean(errors.accountNumbers) || undefined,
                              }}
                              sx={fieldSx}
                            />
                          )}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Chip
                          clickable
                          disabled={!customerAccountOptions.length}
                          label="Select All"
                          onClick={() => {
                            if (allCustomerAccountsSelected) {
                              setField("accountNumbers", []);
                              return;
                            }
                            setField(
                              "accountNumbers",
                              customerAccountOptions.map((opt) => opt.accountNumber)
                            );
                          }}
                          aria-pressed={allCustomerAccountsSelected}
                          aria-label={
                            allCustomerAccountsSelected
                              ? "Deselect all accounts"
                              : "Select all accounts"
                          }
                          sx={selectAllChipSx(allCustomerAccountsSelected)}
                        />
                      </Box>
                      <Typography sx={{ mt: 0.75, fontSize: 12, color: AH_MUTED }}>
                        Restricted to this customer&apos;s authorized AWS accounts
                        {selectedAccountOptions.length
                          ? ` · ${selectedAccountOptions.length} selected`
                          : ""}
                      </Typography>
                      {errors.accountNumbers ? (
                        <Typography sx={{ mt: 0.5, fontSize: 12, color: "#d32f2f" }} role="alert">
                          {errors.accountNumbers}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="complianceCategory">Compliance Category</FieldLabel>
                      <TextField
                        id="complianceCategory"
                        select
                        size="small"
                        fullWidth
                        value={form.complianceCategory || ""}
                        onChange={(event) => setField("complianceCategory", event.target.value)}
                        sx={selectFieldSx(Boolean(form.complianceCategory))}
                        SelectProps={buildSelectProps({ placeholder: "None" })}
                      >
                        <MenuItem value="" sx={menuItemSx}>
                          None
                        </MenuItem>
                        {COMPLIANCE_CATEGORY_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option} sx={menuItemSx}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel htmlFor="placeOfPerformance">Place of Performance</FieldLabel>
                      <TextField
                        id="placeOfPerformance"
                        size="small"
                        fullWidth
                        value={form.placeOfPerformance}
                        onChange={(event) => setField("placeOfPerformance", event.target.value)}
                        placeholder="e.g., AWS GovCloud (US-East)"
                        sx={fieldSx}
                      />
                    </Box>
                    <Box sx={{ minWidth: 0, gridColumn: { sm: "1 / -1" } }}>
                      <FieldLabel htmlFor="statementOfWork">Statement of Work / Packaging Instructions</FieldLabel>
                      <TextField
                        id="statementOfWork"
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        value={form.statementOfWork}
                        onChange={(event) => setField("statementOfWork", event.target.value)}
                        placeholder="Digital delivery protocols, secure data transfer guidelines, and SOW references"
                        sx={fieldSx}
                      />
                    </Box>
                  </Box>

                  {formError ? (
                    <Typography sx={{ mt: 2.5, fontSize: 13, color: "#d32f2f", lineHeight: 1.5 }} role="alert">
                      {formError}
                    </Typography>
                  ) : null}

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mt: 3,
                      pt: 2,
                      borderTop: `1px solid ${AH_BORDER}`,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={leave}
                      disabled={saving}
                      sx={{
                        ...outlinedButtonSx,
                        backgroundColor: "#74788d",
                        color: AH_WHITE,
                        borderColor: "#74788d",
                        "&:hover": { backgroundColor: "#686c7f", borderColor: "#686c7f", boxShadow: "none" },
                      }}
                    >
                      Cancel
                    </Button>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, ml: "auto" }}>
                      <Button
                        variant="outlined"
                        startIcon={<SaveOutlinedIcon />}
                        onClick={() => submit("draft")}
                        disabled={saving}
                        sx={{
                          ...outlinedButtonSx,
                          backgroundColor: AH_WHITE,
                          color: AH_ACCENT,
                          borderColor: AH_ACCENT,
                          "&:hover": {
                            backgroundColor: AH_WHITE,
                            color: AH_ACCENT_HOVER,
                            borderColor: AH_ACCENT_HOVER,
                            boxShadow: "none",
                          },
                        }}
                      >
                        {saving ? "Saving…" : "Save Draft"}
                      </Button>
                      <Button variant="contained" onClick={() => submit("saved")} disabled={saving} sx={primaryButtonSx}>
                        {saving ? "Saving…" : taskOrderId ? "Update Task Order" : "Create Task Order"}
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Container>

        <Dialog
          open={clinDialogOpen}
          onClose={closeClinDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              ...auditHubPageSx,
              borderRadius: "12px",
              overflow: "hidden",
              maxWidth: 560,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              minHeight: 56,
              px: 3,
              backgroundColor: AH_ACCENT,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
              {editingClinId ? "Edit CLIN" : "Add CLIN"}
            </Typography>
            <Box
              component="button"
              type="button"
              aria-label="Close"
              onClick={closeClinDialog}
              sx={{
                p: 0,
                m: 0,
                border: "none",
                background: "transparent",
                color: AH_WHITE,
                fontSize: 28,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              ×
            </Box>
          </Box>
          <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="clin-draft-number">CLIN</FieldLabel>
                  <TextField
                    id="clin-draft-number"
                    size="small"
                    fullWidth
                    value={clinDraft.clinNumber}
                    onChange={(event) => setClinDraftField("clinNumber", event.target.value)}
                    placeholder="e.g., 0001"
                    error={Boolean(clinDraftErrors.clinNumber)}
                    helperText={clinDraftErrors.clinNumber || ""}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="clin-draft-slin">SLIN</FieldLabel>
                  <TextField
                    id="clin-draft-slin"
                    size="small"
                    fullWidth
                    value={clinDraft.slinIdentifier}
                    onChange={(event) => setClinDraftField("slinIdentifier", event.target.value)}
                    placeholder="e.g., 0001AA"
                    sx={fieldSx}
                  />
                </Box>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="clin-draft-description">Description</FieldLabel>
                <TextField
                  id="clin-draft-description"
                  size="small"
                  fullWidth
                  value={clinDraft.description}
                  onChange={(event) => setClinDraftField("description", event.target.value)}
                  placeholder="e.g., Cloud Infrastructure Services"
                  error={Boolean(clinDraftErrors.description)}
                  helperText={clinDraftErrors.description || ""}
                  sx={fieldSx}
                />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="clin-draft-qty">Qty</FieldLabel>
                  <TextField
                    id="clin-draft-qty"
                    size="small"
                    fullWidth
                    value={clinDraft.quantity}
                    onChange={(event) => setClinDraftField("quantity", event.target.value)}
                    placeholder="e.g., 12"
                    error={Boolean(clinDraftErrors.quantity)}
                    helperText={clinDraftErrors.quantity || ""}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="clin-draft-unit">Unit Price ($)</FieldLabel>
                  <TextField
                    id="clin-draft-unit"
                    size="small"
                    fullWidth
                    value={clinDraft.unitPrice}
                    onChange={(event) => setClinDraftField("unitPrice", event.target.value)}
                    onBlur={() => {
                      if (!clinDraft.unitPrice.trim() || clinDraftErrors.unitPrice) return;
                      const amount = parseNumber(clinDraft.unitPrice);
                      if (amount != null && !Number.isNaN(amount)) {
                        setClinDraftField("unitPrice", formatMoneyDisplay(clinDraft.unitPrice));
                      }
                    }}
                    placeholder="0.00"
                    error={Boolean(clinDraftErrors.unitPrice)}
                    helperText={clinDraftErrors.unitPrice || ""}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="clin-draft-funded">Funded ($)</FieldLabel>
                  <TextField
                    id="clin-draft-funded"
                    size="small"
                    fullWidth
                    value={clinDraft.fundedAmount}
                    onChange={(event) => setClinDraftField("fundedAmount", event.target.value)}
                    onBlur={() => {
                      if (!clinDraft.fundedAmount.trim() || clinDraftErrors.fundedAmount) return;
                      const amount = parseNumber(clinDraft.fundedAmount);
                      if (amount != null && !Number.isNaN(amount)) {
                        setClinDraftField("fundedAmount", formatMoneyDisplay(clinDraft.fundedAmount));
                      }
                    }}
                    placeholder="0.00"
                    error={Boolean(clinDraftErrors.fundedAmount)}
                    helperText={clinDraftErrors.fundedAmount || ""}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel htmlFor="clin-draft-acctg">Accounting Code</FieldLabel>
                  <TextField
                    id="clin-draft-acctg"
                    size="small"
                    fullWidth
                    value={clinDraft.accountingCode}
                    onChange={(event) => setClinDraftField("accountingCode", event.target.value)}
                    placeholder="e.g., 97X4930.TF"
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 3,
              py: 2,
              borderTop: `1px solid ${AH_BORDER}`,
              backgroundColor: "#f8fafc",
            }}
          >
            <Button
              variant="contained"
              onClick={closeClinDialog}
              sx={{
                ...primaryButtonSx,
                height: 36,
                minHeight: 36,
                backgroundColor: "#74788d",
                color: "#eff2f7",
                "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveClinDraft}
              sx={{ ...primaryButtonSx, height: 36, minHeight: 36 }}
            >
              {editingClinId ? "Save" : "Create"}
            </Button>
          </Box>
        </Dialog>
      </div>
    </AuditHubThemeProvider>
  );
};

export default AddTaskOrder;
